import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { getProviderConfig, updateProviderConfig, type ProviderConfig } from '@/api/billingAdmin'
import {
  getPlatformPayments, testPlatformPayments, updatePlatformPayments,
} from '@/api/instanceAdmin'
import { Field, InfoBox, errText } from '../adminUi'

/**
 * The gateway: which provider, its credentials, and the webhook secret.
 *
 * This replaces the old top-level Payments tab — gateway configuration now
 * lives with everything else about money, in one place.
 */
export default function PaymentProviderPanel() {
  const [cfg, setCfg] = useState<ProviderConfig | null>(null)
  const [keyId, setKeyId] = useState('')
  const [keySecret, setKeySecret] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [test, setTest] = useState<{ success: boolean; message: string } | null>(null)

  const load = useCallback(async () => {
    try {
      const [provider, payments] = await Promise.all([
        getProviderConfig(),
        getPlatformPayments().catch(() => null),
      ])
      setCfg(provider)
      setKeyId(provider.gateway.key_id || '')
      setEnabled(payments?.payments_enabled ?? provider.gateway.payments_enabled ?? true)
    } catch (e) {
      toast.error(errText(e, 'Could not load the payment provider'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const saveGateway = async () => {
    setSaving(true)
    try {
      await updatePlatformPayments({
        key_id: keyId.trim(),
        // Blank means "keep the stored secret" — the field never shows it back.
        key_secret: keySecret ? keySecret : undefined,
        payments_enabled: enabled,
      })
      if (webhookSecret) {
        await updateProviderConfig({ webhook_secret: webhookSecret })
        setWebhookSecret('')
      }
      setKeySecret('')
      toast.success('Gateway settings saved')
      await load()
    } catch (e) {
      toast.error(errText(e, 'Could not save the gateway settings'))
    } finally {
      setSaving(false)
    }
  }

  const changeProvider = async (name: string) => {
    try {
      setCfg(await updateProviderConfig({ provider: name }))
      toast.success(`Provider set to ${name}`)
    } catch (e) {
      toast.error(errText(e, 'Could not change the provider'))
    }
  }

  const runTest = async () => {
    setTest(null)
    try {
      setTest(await testPlatformPayments())
    } catch (e) {
      setTest({ success: false, message: errText(e, 'The test failed') })
    }
  }

  if (loading || !cfg) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spin" /></div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', maxWidth: 620 }}>
      <InfoBox>
        Payments are provider-agnostic. Only the selected provider's credentials
        are used; switching later needs no data migration because every gateway id
        is stored per order.
      </InfoBox>

      <Field label="Provider">
        <select value={cfg.provider} onChange={(e) => changeProvider(e.target.value)} style={{ width: '100%' }}>
          {cfg.available.map((p) => (
            <option key={p.name} value={p.name} disabled={!p.enabled}>
              {p.name}{p.enabled ? '' : ' (coming soon)'}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Key ID" hint="Public credential — safe to expose to the browser at checkout.">
        <input value={keyId} onChange={(e) => setKeyId(e.target.value)}
               placeholder="rzp_live_..." style={{ width: '100%', fontFamily: 'monospace' }} />
      </Field>

      <Field label="Key secret"
             hint={cfg.gateway.has_key_secret
               ? 'A secret is stored. Leave blank to keep it; type to replace.'
               : 'No secret stored yet — checkout will not work until one is saved.'}>
        <input type="password" value={keySecret} onChange={(e) => setKeySecret(e.target.value)}
               placeholder={cfg.gateway.has_key_secret ? '••••••••' : 'Paste the key secret'}
               style={{ width: '100%' }} />
      </Field>

      <Field
        label="Webhook signing secret"
        hint={cfg.has_webhook_secret
          ? 'A secret is stored. Leave blank to keep it; type to replace.'
          : 'REQUIRED. Without it every webhook delivery is rejected — which means a payment captured after the browser closed would never grant its credits.'}
      >
        <input type="password" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)}
               placeholder={cfg.has_webhook_secret ? '••••••••' : 'Paste the webhook secret'}
               style={{ width: '100%' }} />
      </Field>

      <div style={{
        fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
        background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
        borderRadius: 6, padding: '0.625rem 0.75rem',
      }}>
        Point the gateway's webhook at{' '}
        <code style={{ fontFamily: 'monospace' }}>
          {typeof window !== 'undefined' ? window.location.origin : ''}/api/payments/webhook/{cfg.provider}
        </code>{' '}
        and subscribe to <code>payment.captured</code>, <code>order.paid</code>,{' '}
        <code>subscription.charged</code>, <code>subscription.halted</code>,{' '}
        <code>payment.failed</code> and <code>refund.processed</code>.
      </div>

      <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.875rem' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Payments enabled
        <span style={{ color: 'var(--color-text-secondary)' }}>
          — off switch for paid plans without deleting the keys.
        </span>
      </label>

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button className="btn btn-primary" onClick={saveGateway} disabled={saving}>
          {saving ? <Loader2 size={14} className="spin" /> : 'Save'}
        </button>
        <button className="btn btn-secondary" onClick={runTest}>Test connection</button>
      </div>

      {test && (
        <div style={{
          display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.875rem',
          color: test.success ? 'var(--color-success, #2e9e5b)' : 'var(--color-danger, #d64545)',
        }}>
          {test.success ? <CheckCircle2 size={16} /> : <XCircle size={16} />} {test.message}
        </div>
      )}

      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
        Checkout is currently{' '}
        <strong>{cfg.gateway.effective_enabled ? 'working' : 'unavailable'}</strong>
        {!cfg.gateway.effective_enabled && ' — a key id, a key secret and the enabled switch are all required.'}
      </div>
    </div>
  )
}
